import { useEffect, useState } from 'react';
import api, { formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { ShieldCheck, Smartphone, Trash2, Info, AlertTriangle } from 'lucide-react';

function fmt(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return s; }
}

export default function TrustedDevicesSection() {
  const { user } = useAuth();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(null);

  const currentDeviceToken = (() => {
    try { return localStorage.getItem('device_token'); } catch { return null; }
  })();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/auth/trusted-devices');
      setDevices(data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Failed to load trusted devices'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const revoke = async (id) => {
    if (!window.confirm('Revoke this trusted device? You\'ll need an OTP next time you sign in from it.')) return;
    setRemoving(id);
    try {
      await api.delete(`/auth/trusted-devices/${id}`);
      toast.success('Trusted device revoked');
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Failed to revoke'); }
    finally { setRemoving(null); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="trusted-devices-section">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0">
          <ShieldCheck className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Security</div>
          <h2 className="font-heading text-xl font-semibold text-slate-900 mt-1">Trusted devices</h2>
          <p className="text-sm text-slate-500 mt-1">
            Devices you trusted while signing in. We&apos;ll skip the email OTP for 3 days on each trusted device.
          </p>
        </div>
      </div>

      {!user?.mfa_enabled && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-900 flex items-start gap-2" data-testid="mfa-disabled-notice">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <b>Multi-factor authentication is currently OFF on your account</b> — that&apos;s why no &ldquo;Trust this device&rdquo; checkbox appears at sign-in. MFA gets turned on automatically the first time you change your password via the <i>Change password</i> screen. Until then, every sign-in just uses your password (no OTP, no trusted-device check).
          </div>
        </div>
      )}

      {user?.mfa_enabled && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-900 flex items-start gap-2">
          <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <div>
            Tip — on your next sign-in, check <b>&ldquo;Trust this device for 3 days&rdquo;</b> on the OTP screen to skip the verification code from that browser.
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-400 py-4">Loading…</div>
      ) : devices.length === 0 ? (
        <div className="text-sm text-slate-500 py-6 text-center bg-slate-50 rounded-xl border border-dashed border-slate-200">
          No trusted devices yet — sign in with MFA and tick &ldquo;Trust this device&rdquo; to add one.
        </div>
      ) : (
        <div className="space-y-2" data-testid="trusted-devices-list">
          {devices.map(d => {
            const isCurrent = currentDeviceToken && d.id === currentDeviceToken;
            return (
              <div key={d.id} data-testid={`trusted-device-${d.id}`}
                   className={`flex items-start justify-between gap-3 rounded-xl border p-4 ${isCurrent ? 'border-emerald-300 bg-emerald-50/40' : 'border-slate-200 bg-white'}`}>
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <Smartphone className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 text-sm flex items-center gap-2 flex-wrap">
                      {d.label || 'Browser session'}
                      {isCurrent && (
                        <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                          This device
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-1 space-y-0.5">
                      {d.user_agent && <div className="truncate">{d.user_agent}</div>}
                      <div>
                        Added <b>{fmt(d.created_at)}</b>
                        {d.last_used_at ? <> · last used <b>{fmt(d.last_used_at)}</b></> : null}
                      </div>
                      {d.expires_at && <div className="text-amber-700">Expires {fmt(d.expires_at)}</div>}
                    </div>
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => revoke(d.id)} disabled={removing === d.id}
                        data-testid={`revoke-trusted-${d.id}`}
                        className="rounded-full h-8 text-red-600 border-red-300 hover:bg-red-50 flex-shrink-0">
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" /> {removing === d.id ? 'Revoking…' : 'Revoke'}
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
